import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

/** @type {WeakMap<HTMLElement, () => void>} */
const resizeHandlers = new WeakMap();

/** @type {WeakMap<HTMLElement, number>} last equalized height per section */
const appliedHeights = new WeakMap();

/**
 * @param {HTMLElement} section
 */
export function equalizeIndustryCardHeights(section) {
	const cards = [...section.querySelectorAll('.industries__card')];
	if (!cards.length) {
		return;
	}

	cards.forEach((card) => {
		card.style.minHeight = '';
	});

	const maxHeight = Math.max(
		...cards.map((card) => Math.ceil(card.getBoundingClientRect().height)),
		0,
	);

	if (maxHeight > 0) {
		const heightPx = `${maxHeight}px`;
		cards.forEach((card) => {
			card.style.minHeight = heightPx;
		});
	}

	// Poke Swiper only when the equalized height actually changed: iOS fires
	// height-only resize storms while the toolbar collapses during vertical
	// page scroll, and in cssMode swiper.update() writes wrapper.scrollLeft,
	// so churning it fought native scroll-snap and jittered the carousel.
	const previous = appliedHeights.get(section);
	appliedHeights.set(section, maxHeight);
	if (previous === maxHeight) {
		return;
	}

	const swiperEl = section.querySelector('.js-industries-swiper');
	if (swiperEl?.swiper) {
		swiperEl.swiper.update();
	}
}

/**
 * @param {HTMLElement} section
 */
function bindEqualHeightResize(section) {
	const existing = resizeHandlers.get(section);
	if (existing) {
		window.removeEventListener('resize', existing);
	}

	let frame = 0;
	// Height-only resizes are the iOS toolbar during vertical page scroll —
	// card heights depend on the viewport WIDTH, so only re-equalize when it
	// actually changed.
	let lastWidth = window.innerWidth;
	const onResize = () => {
		if (window.innerWidth === lastWidth) {
			return;
		}
		lastWidth = window.innerWidth;
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(() => equalizeIndustryCardHeights(section));
	};

	resizeHandlers.set(section, onResize);
	window.addEventListener('resize', onResize, { passive: true });
}

/**
 * @param {import('swiper').Swiper} swiper
 * @param {HTMLButtonElement | null} prevBtn
 * @param {HTMLButtonElement | null} nextBtn
 */
function syncNavButtons(swiper, prevBtn, nextBtn) {
	if (!prevBtn || !nextBtn) {
		return;
	}

	const prevDisabled = swiper.isLocked || swiper.isBeginning;
	const nextDisabled = swiper.isLocked || swiper.isEnd;

	prevBtn.disabled = prevDisabled;
	nextBtn.disabled = nextDisabled;
	prevBtn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
	nextBtn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
	prevBtn.classList.toggle('swiper-button-disabled', prevDisabled);
	nextBtn.classList.toggle('swiper-button-disabled', nextDisabled);
}

/**
 * @param {HTMLElement} section
 */
function initSectionSwiper(section) {
	const el = section.querySelector('.js-industries-swiper');
	const prevBtn = section.querySelector('.js-industries-prev');
	const nextBtn = section.querySelector('.js-industries-next');
	const nav = section.querySelector('.industries__nav');
	const slides = el?.querySelectorAll('.swiper-slide') ?? [];

	if (!el) {
		return null;
	}

	bindEqualHeightResize(section);

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(() => {
			requestAnimationFrame(() => equalizeIndustryCardHeights(section));
		});
		section.querySelectorAll('.industries__card-copy').forEach((panel) => observer.observe(panel));
	}

	requestAnimationFrame(() => equalizeIndustryCardHeights(section));

	if (slides.length < 2) {
		nav?.setAttribute('hidden', 'hidden');
		return null;
	}

	nav?.removeAttribute('hidden');

	// iOS/WebKit chokes on transform-mode carousels (blank/frozen slides,
	// dead nav — see client-testimonials-swiper.js for the history). Native
	// scroll + scroll-snap on touch devices sidesteps that bug family.
	const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;

	const swiper = new Swiper(el, {
		modules: [Navigation],
		slidesPerView: 'auto',
		spaceBetween: 20,
		watchOverflow: true,
		cssMode: isCoarsePointer,
		// Keep the drag on the carousel instead of losing it to iOS Safari's
		// edge swipe-back gesture (#128).
		edgeSwipeDetection: 'prevent',
		observer: true,
		observeParents: true,
		navigation: {
			prevEl: prevBtn,
			nextEl: nextBtn,
		},
		breakpoints: {
			640: {
				spaceBetween: 20,
			},
			1100: {
				spaceBetween: 20,
			},
		},
		on: {
			init(swiperInstance) {
				requestAnimationFrame(() => {
					equalizeIndustryCardHeights(section);
					syncNavButtons(swiperInstance, prevBtn, nextBtn);
				});
			},
			slideChange(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
			reachBeginning(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
			reachEnd(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
			fromEdge(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
			resize(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
			lock(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
			unlock(swiperInstance) {
				syncNavButtons(swiperInstance, prevBtn, nextBtn);
			},
		},
	});

	requestAnimationFrame(() => equalizeIndustryCardHeights(section));

	return swiper;
}

export function initIndustriesSwiper() {
	document.querySelectorAll('.js-industries-section').forEach((section) => {
		const el = section.querySelector('.js-industries-swiper');
		if (!el || el.swiper) {
			return;
		}

		initSectionSwiper(section);
	});
}
